import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Activity, AlertTriangle, ChevronRight, Heart } from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/EmptyState";
import { Link } from "react-router-dom";

function colorClasses(color) {
  if (color === "green") return {
    bar: "bg-emerald-500", ring: "text-emerald-500",
    pill: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  };
  if (color === "orange") return {
    bar: "bg-amber-500", ring: "text-amber-500",
    pill: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  };
  return {
    bar: "bg-rose-500", ring: "text-rose-500",
    pill: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30",
  };
}

export function HealthScoreCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/inventory-health");
      setData(data);
    } catch (e) {
      // soft fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Auto-refresh every 60s alongside the dashboard polling cadence
  useEffect(() => {
    const id = setInterval(fetchData, 60_000);
    return () => clearInterval(id);
  }, [fetchData]);

  if (loading && !data) {
    return <Skeleton className="h-28" />;
  }

  const score = data?.score ?? 0;
  const cls = colorClasses(data?.color || "green");
  const needs = data?.needs_attention || 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="text-left rounded-2xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors w-full"
          data-testid="health-score-card"
          onClick={() => setOpen(true)}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Heart className={`w-4 h-4 ${cls.ring}`} /> Inventory Health Score
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="flex items-baseline gap-2">
            <div className={`text-4xl font-semibold tabular-nums ${cls.ring}`} data-testid="health-score-value">{score}%</div>
            <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls.pill}`} data-testid="health-score-color">
              {data?.color}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden" aria-hidden="true">
            <div className={`h-full ${cls.bar} transition-all duration-500`} style={{ width: `${score}%` }} />
          </div>
          <div className="mt-2 text-xs text-muted-foreground" data-testid="health-score-summary">
            {needs === 0 ? "All good — no action needed." : `${needs} item${needs === 1 ? "" : "s"} need attention.`}
          </div>
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="health-score-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Heart className={`w-5 h-5 ${cls.ring}`} /> Inventory Health Score
          </SheetTitle>
          <SheetDescription>
            {data?.total_items} items analyzed • {needs} need attention
          </SheetDescription>
        </SheetHeader>

        <div className={`mt-4 rounded-xl border p-4 ${cls.pill}`}>
          <div className="text-4xl font-semibold tabular-nums">{score}%</div>
          <div className="text-xs uppercase tracking-wider mt-1">{data?.color} status</div>
        </div>

        {data?.actions?.length > 0 && (
          <div className="mt-4 rounded-xl border border-border bg-card p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Suggested actions
            </div>
            <ul className="space-y-1.5 text-sm">
              {data.actions.map((a, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`health-action-${i}`}>
                  <ChevronRight className="w-3.5 h-3.5 mt-1 text-primary shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <BreakdownSection title="Out of stock" items={data?.issues?.out_of_stock} tone="rose" testid="health-out-of-stock" />
        <BreakdownSection title="Low stock (≤ minimum)" items={data?.issues?.low_stock} tone="amber" testid="health-low-stock" />
        <BreakdownSection title="Below reorder threshold" items={data?.issues?.below_reorder} tone="amber" testid="health-below-reorder" />
        <BreakdownSection title="Expired lots" items={data?.issues?.expired} tone="rose" testid="health-expired" />
        <BreakdownSection title={`Expiring soon (≤ ${data?.thresholds?.expiring_window_days ?? 30} days)`} items={data?.issues?.expiring} tone="amber" testid="health-expiring" />
        <BreakdownSection title={`Slow-moving (no movement in ${data?.thresholds?.slow_moving_days ?? 60} days)`} items={data?.issues?.slow_moving} tone="slate" testid="health-slow-moving" />
        <BreakdownSection title="Missing data" items={data?.issues?.missing_data} tone="slate" testid="health-missing" missingField />

        <div className="mt-6 pb-2">
          <Link to="/app/inventory" className="text-sm text-primary hover:underline" onClick={() => setOpen(false)} data-testid="health-view-inventory">
            Open Inventory
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BreakdownSection({ title, items, tone, testid, missingField }) {
  if (!items || items.length === 0) return null;
  const toneCls = tone === "rose"
    ? "text-rose-600 dark:text-rose-400"
    : tone === "amber"
    ? "text-amber-600 dark:text-amber-400"
    : "text-muted-foreground";
  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-sm font-medium flex items-center gap-1.5 ${toneCls}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> {title}
        </div>
        <div className="text-xs font-mono text-muted-foreground">{items.length}</div>
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto -mx-1">
        {items.slice(0, 50).map((it) => (
          <div key={`${testid}-${it.item_id}`} className="px-1 py-1.5 flex items-center justify-between text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{it.name || it.item_id}</div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="font-mono">{it.sku}</span>
                {it.category && <span>• {it.category}</span>}
                {missingField && it.missing && <span className="text-amber-600 dark:text-amber-400">missing: {it.missing.join(", ")}</span>}
                {it.lead_time_label && <span>• ⏱ {it.lead_time_label}</span>}
              </div>
            </div>
            <div className={`text-xs font-mono shrink-0 ${toneCls}`}>
              {it.quantity} / {it.min_stock || 0}
            </div>
          </div>
        ))}
        {items.length > 50 && (
          <div className="px-1 py-1.5 text-xs text-muted-foreground">+ {items.length - 50} more…</div>
        )}
      </div>
    </div>
  );
}
